import React, { ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export type CustomTooltipPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

export interface CustomTooltipProps {
  tooltipText: ReactNode;
  children: ReactNode;
  placement?: CustomTooltipPlacement;
  tooltipClass?: string;
}

/**
 * TypeScript CustomTooltip for PIM. Wraps a single interactive child with a
 * react-bootstrap tooltip; Popper handles viewport flipping + overflow padding
 * so the tooltip escapes clipping ancestors (e.g. the PIM top bar). Falls back
 * to rendering children untouched when the child is not a single valid element.
 */
export function CustomTooltip({ tooltipText, children, placement = 'bottom', tooltipClass = 'custom-tooltip' }: CustomTooltipProps): React.ReactElement | null {
  const tooltipId = React.useId();

  if (!children) {
    return null;
  }

  if (Array.isArray(children)) {
    return <div>{children}</div>;
  }

  const childCount = React.Children.count(children);
  if (childCount > 1) {
    return <div>{children}</div>;
  }

  if (childCount === 0) {
    return null;
  }

  if (!React.isValidElement(children)) {
    return <>{children}</>;
  }

  if (children.type === React.Fragment) {
    return <div>{children}</div>;
  }

  try {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement={placement}
        flip
        popperConfig={{ modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }] }}
        overlay={<Tooltip id={tooltipId} className={tooltipClass}>{tooltipText}</Tooltip>}
      >
        {children}
      </OverlayTrigger>
    );
  } catch {
    return <>{children}</>;
  }
}

export default CustomTooltip;
